import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { userApi, User, UserListParams } from '../../../services/admin/userApi';
import { getDepartments, getUniversities } from '../../../services/personApi';
import { AdminPage, AdminPageHeader, AdminPageTitle, AdminSection } from '../common/AdminPrimitives';
import {
  ActionButton,
  ActionButtons,
  BodyCell,
  CellText,
  EmptyText,
  HeaderCell,
  HeaderText,
  RetryButton,
  TableBody,
  TableContainer,
  TableHeader,
  TableHeaderRow,
  TableInner,
  TableRow,
} from '../common/AdminTablePrimitives';
import {
  CloseButton,
  FormGroup,
  FormLabel,
  InputField,
  ModalBody,
  ModalCard,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  PrimaryButton,
  ReadonlyField,
  SecondaryButton,
  SidePanelBody,
  SidePanelFooter,
  SidePanelHeader,
  SidePanelModal,
  SidePanelOverlay,
} from '../common/AdminModalPrimitives';
import AdminFilterDropdown from '../common/AdminFilterDropdown';
import CommunityTablePagination from '../community/CommunityTablePagination';

const UserQualificationComponent: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIdentity, setSelectedIdentity] = useState<'STUDENT' | 'DOCTOR' | 'ADMIN' | 'OTHER' | 'all'>('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [searchQuery] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0,
  });
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showCertModal, setShowCertModal] = useState(false);
  const [certUserDetails, setCertUserDetails] = useState<any>(null);
  const [affiliations, setAffiliations] = useState<string[]>([]);
  const [affiliationsLoading, setAffiliationsLoading] = useState(false);

  const fetchPendingUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: UserListParams = {
        page: pagination.page,
        size: pagination.size,
        identity: selectedIdentity !== 'all' ? selectedIdentity : undefined,
        department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
        search: searchQuery || undefined,
      };

      const response = await userApi.getPendingUsers(params);
      setUsers(response.content);
      setPagination((prev) => ({
        ...prev,
        totalPages: response.totalPages,
        totalElements: response.totalElements,
      }));
    } catch (err) {
      setError('인증 대기 사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, searchQuery, selectedDepartment, selectedIdentity]);

  const fetchAffiliations = useCallback(async (identity: 'STUDENT' | 'DOCTOR' | 'all') => {
    if (identity === 'all') {
      setAffiliations([]);
      return;
    }

    try {
      setAffiliationsLoading(true);
      let affiliationList: string[] = [];

      if (identity === 'STUDENT') {
        affiliationList = await getUniversities();
      } else if (identity === 'DOCTOR') {
        affiliationList = await getDepartments();
      }

      setAffiliations(affiliationList);
    } catch (err) {
      setAffiliations([]);
    } finally {
      setAffiliationsLoading(false);
    }
  }, []);

  const handleIdentityChange = (identity: 'STUDENT' | 'DOCTOR' | 'ADMIN' | 'OTHER' | 'all') => {
    setSelectedIdentity(identity);
    setSelectedDepartment('all');
    setPagination((prev) => ({ ...prev, page: 0 }));

    if (identity === 'STUDENT' || identity === 'DOCTOR') {
      fetchAffiliations(identity);
      return;
    }

    setAffiliations([]);
  };

  useEffect(() => {
    fetchPendingUsers();
  }, [fetchPendingUsers]);

  const handleApprove = async (userId: number) => {
    try {
      await userApi.approveUser(userId);
      alert('사용자가 승인되었습니다.');
      fetchPendingUsers();
    } catch (err) {
      alert('사용자 승인에 실패했습니다.');
    }
  };

  const handleReject = (user: User) => {
    setSelectedUser(user);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleConfirmReject = async () => {
    if (!selectedUser || !rejectReason.trim()) {
      alert('반려 사유를 입력해주세요.');
      return;
    }

    try {
      await userApi.rejectUser(selectedUser.id, rejectReason);
      alert('사용자가 반려되었습니다.');
      setShowRejectModal(false);
      setSelectedUser(null);
      setRejectReason('');
      fetchPendingUsers();
    } catch (err) {
      alert('사용자 반려에 실패했습니다.');
    }
  };

  const handleCloseRejectModal = () => {
    setShowRejectModal(false);
    setSelectedUser(null);
    setRejectReason('');
  };

  const handleCertificateClick = async (user: User) => {
    try {
      const response = await userApi.getUserDetails(user.id);
      setCertUserDetails({
        person_info: {
          person_id: response.id,
          name: response.name,
          email: response.email,
          identity:
            response.category === '학생'
              ? 'STUDENT'
              : response.category === '의사'
                ? 'DOCTOR'
                : response.category === '관리자'
                  ? 'ADMIN'
                  : 'OTHER',
          department: response.department,
          license: response.license || null,
          id_card_file_url: response.idCardFileUrl || null,
          birth_date: response.birthDate,
          phone_number: response.phone,
          created_at: response.createdAt,
          post_count: response.postsCount,
          reply_count: response.commentsCount,
          deactivate_reason: response.deactivateReason,
          last_login_at: response.lastLoginAt || null,
        },
      });
      setShowCertModal(true);
    } catch (err) {
      alert('인증 서류 정보를 불러오는데 실패했습니다.');
    }
  };

  const handleCloseCertModal = () => {
    setShowCertModal(false);
    setCertUserDetails(null);
  };

  const handleCertApprove = async () => {
    if (!certUserDetails) {
      return;
    }

    try {
      await userApi.approveUser(certUserDetails.person_info.person_id);
      alert('사용자가 승인되었습니다.');
      setShowCertModal(false);
      setCertUserDetails(null);
      fetchPendingUsers();
    } catch (err) {
      alert('승인 처리에 실패했습니다.');
    }
  };

  const handleCertReject = () => {
    setShowCertModal(false);
    if (!certUserDetails) {
      return;
    }

    setSelectedUser({
      id: certUserDetails.person_info.person_id,
      name: certUserDetails.person_info.name,
      email: certUserDetails.person_info.email,
      category: certUserDetails.person_info.identity,
      department: certUserDetails.person_info.department,
      phone: certUserDetails.person_info.phone_number,
      affiliation: certUserDetails.person_info.department || '',
      status: 'PENDING',
      createdAt: certUserDetails.person_info.created_at || new Date().toISOString(),
      postsCount: certUserDetails.person_info.post_count || 0,
      commentsCount: certUserDetails.person_info.reply_count || 0,
    });
    setShowRejectModal(true);
  };

  const isInitialLoading = loading && users.length === 0;
  if (isInitialLoading) {
    return (
      <AdminPage>
        <AdminPageHeader>
          <AdminPageTitle>회원 자격 관리</AdminPageTitle>
        </AdminPageHeader>
        <AdminSection>
          <StateMessage>로딩 중...</StateMessage>
        </AdminSection>
      </AdminPage>
    );
  }

  if (error && users.length === 0) {
    return (
      <AdminPage>
        <AdminPageHeader>
          <AdminPageTitle>회원 자격 관리</AdminPageTitle>
        </AdminPageHeader>
        <AdminSection>
          <StateMessage>{error}</StateMessage>
          <RetryButton onClick={fetchPendingUsers}>다시 시도</RetryButton>
        </AdminSection>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader>
        <AdminPageTitle>회원 자격 관리</AdminPageTitle>
      </AdminPageHeader>

      <AdminSection>
        <FilterSection>
          <FilterGroup>
            <AdminFilterDropdown
              value={selectedIdentity}
              onChange={(value) => handleIdentityChange(value as any)}
              maxWidth="140px"
              options={[
                { value: 'all', label: '구분' },
                { value: 'STUDENT', label: '학생' },
                { value: 'DOCTOR', label: '의사' },
                { value: 'ADMIN', label: '관리자' },
                { value: 'OTHER', label: '기타' },
              ]}
            />

            {selectedIdentity !== 'ADMIN' && selectedIdentity !== 'OTHER' && (
              <AdminFilterDropdown
                value={selectedDepartment}
                onChange={setSelectedDepartment}
                disabled={affiliationsLoading || affiliations.length === 0}
                maxWidth="180px"
                options={[
                  {
                    value: 'all',
                    label: affiliationsLoading
                      ? '로딩 중...'
                      : selectedIdentity === 'all'
                        ? '소속'
                        : affiliations.length === 0
                          ? '소속 정보 없음'
                          : '소속',
                  },
                  ...affiliations.map((affiliation) => ({ value: affiliation, label: affiliation })),
                ]}
              />
            )}
          </FilterGroup>
        </FilterSection>

        <TableContainer>
          <TableInner>
            <TableHeader>
              <TableHeaderRow $columns="60px minmax(100px, 2fr) minmax(100px, 2fr) minmax(100px, 2fr) 130px 100px">
                <HeaderCell><HeaderText>번호</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>이름</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>구분</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>소속</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>인증 서류</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>관리</HeaderText></HeaderCell>
              </TableHeaderRow>
            </TableHeader>

            <TableBody>
              {users.length > 0 ? (
                users.map((user, index) => (
                  <TableRow key={user.id} $columns="60px minmax(100px, 2fr) minmax(100px, 2fr) minmax(100px, 2fr) 130px 100px">
                    <BodyCell>
                      <CellText>{pagination.totalElements - pagination.page * pagination.size - index}</CellText>
                    </BodyCell>
                    <BodyCell>
                      <CellText>{user.name}</CellText>
                    </BodyCell>
                    <BodyCell>
                      <CellText>{user.category}</CellText>
                    </BodyCell>
                    <BodyCell>
                      <CellText>{user.department}</CellText>
                    </BodyCell>
                    <BodyCell>
                      <ActionButton $tone="primary" onClick={() => handleCertificateClick(user)}>
                        인증 서류 확인
                      </ActionButton>
                    </BodyCell>
                    <BodyCell>
                      <ActionButtons>
                        <ActionButton $tone="primary" onClick={() => handleApprove(user.id)}>
                          승인
                        </ActionButton>
                        <ActionButton $tone="danger" onClick={() => handleReject(user)}>
                          반려
                        </ActionButton>
                      </ActionButtons>
                    </BodyCell>
                  </TableRow>
                ))
              ) : (
                <EmptyStateRow>
                  <EmptyText>대기 중인 사용자가 없습니다.</EmptyText>
                </EmptyStateRow>
              )}
            </TableBody>
          </TableInner>
          <CommunityTablePagination
            currentPage={pagination.page + 1}
            totalPages={pagination.totalPages}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, page: page - 1 }))}
          />
        </TableContainer>
      </AdminSection>

      {showRejectModal && selectedUser && (
        <RejectModal
          userName={selectedUser.name}
          rejectReason={rejectReason}
          onReasonChange={setRejectReason}
          onCancel={handleCloseRejectModal}
          onConfirm={handleConfirmReject}
        />
      )}

      {showCertModal && certUserDetails && (
        <CertificateModal
          userDetails={certUserDetails}
          onClose={handleCloseCertModal}
          onApprove={handleCertApprove}
          onReject={handleCertReject}
        />
      )}
    </AdminPage>
  );
};

interface RejectModalProps {
  userName: string;
  rejectReason: string;
  onReasonChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

const RejectModal: React.FC<RejectModalProps> = ({
  userName,
  rejectReason,
  onReasonChange,
  onCancel,
  onConfirm,
}) => (
  <ModalOverlay>
    <ModalCard $maxWidth="420px">
      <ModalHeader>
        <ModalTitle>회원 자격 반려</ModalTitle>
        <CloseButton onClick={onCancel}>×</CloseButton>
      </ModalHeader>
      <ModalBody>
        <ModalDescription>
          <strong>{userName}</strong> 회원의 자격 인증을 반려합니다.
        </ModalDescription>
        <FormGroup>
          <FormLabel>반려 사유</FormLabel>
          <InputField
            type="text"
            placeholder="반려 사유를 입력해주세요."
            value={rejectReason}
            onChange={(e) => onReasonChange(e.target.value)}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <SecondaryButton onClick={onCancel}>취소</SecondaryButton>
        <PrimaryButton onClick={onConfirm}>반려하기</PrimaryButton>
      </ModalFooter>
    </ModalCard>
  </ModalOverlay>
);

interface CertificateModalProps {
  userDetails: any;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
}

const CertificateModal: React.FC<CertificateModalProps> = ({ userDetails, onClose, onApprove, onReject }) => {
  const personInfo = userDetails.person_info;
  const isDoctor = personInfo.identity === 'DOCTOR';

  return (
    <SidePanelOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <SidePanelModal>
        <SidePanelHeader>
          <ModalTitle>회원 자격 인증 서류</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </SidePanelHeader>

        <SidePanelBody>
          <CertInfoGrid>
            <FormGroup>
              <FormLabel>이름</FormLabel>
              <ReadonlyField>{personInfo.name}</ReadonlyField>
            </FormGroup>
            <FormGroup>
              <FormLabel>구분</FormLabel>
              <ReadonlyField>{personInfo.identity === 'STUDENT' ? '학생' : '의사'}</ReadonlyField>
            </FormGroup>
            <FormGroup>
              <FormLabel>소속</FormLabel>
              <ReadonlyField>{personInfo.department}</ReadonlyField>
            </FormGroup>
          </CertInfoGrid>

          {!isDoctor && personInfo.id_card_file_url && (
            <FormGroup>
              <FormLabel>서류</FormLabel>
              <DocumentImage src={personInfo.id_card_file_url} alt="인증 서류" />
            </FormGroup>
          )}

          {isDoctor && (
            <>
              <FormGroup>
                <FormLabel>생년월일</FormLabel>
                <ReadonlyField>{personInfo.birth_date ? personInfo.birth_date.replace(/-/g, '').slice(2) : ''}</ReadonlyField>
              </FormGroup>
              <FormGroup>
                <FormLabel>면허번호</FormLabel>
                <ReadonlyField>{personInfo.license || ''}</ReadonlyField>
              </FormGroup>
            </>
          )}
        </SidePanelBody>

        <SidePanelFooter>
          <SecondaryButton onClick={onReject}>반려하기</SecondaryButton>
          <PrimaryButton onClick={onApprove}>승인하기</PrimaryButton>
        </SidePanelFooter>
      </SidePanelModal>
    </SidePanelOverlay>
  );
};

const FilterSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const StateMessage = styled.div`
  padding: 40px;
  color: #666;
  font-size: 16px;
  text-align: center;
`;

const EmptyStateRow = styled.div`
  padding: 48px 20px;
  display: flex;
  justify-content: center;
`;

const ModalDescription = styled.p`
  margin: 0 0 20px;
  color: #171719;
  font-size: 16px;
  line-height: 24px;
`;

const CertInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DocumentImage = styled.img`
  width: 100%;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  object-fit: contain;
  background: #f7f7f8;
`;

export default UserQualificationComponent;
